import { PRIVATE_API_ROUTES } from "../constants";
import Http from "./http-service";

import {
  downloadReports,
  getLocalizedMessage,
  hideLoaderShowErrorMsg,
} from "../UtilityFunctions/utils";
import { showNotification } from "../UtilityFunctions/notification";
import {
  getQuestionnaireList as getQuestionnaireListAction,
  getPrimaryQuestionnaire,
  getQuestionnaireById,
} from "../globalContext/questionnaire/questionnaireActions";
import { setLoading } from "../globalContext/globalLoader/globalLoaderAction";

class QuestionnaireService {
  static async getPrimaryQuestionnaire({
    questionnaireDispatch,
    loaderDispatch,
    params,
    intl,
  }) {
    loaderDispatch(setLoading(true));
    return Http.get(PRIVATE_API_ROUTES.CHECK_QUESTIONNAIRE, params)
      .then(({ data }) => {
        loaderDispatch(setLoading(false));
        questionnaireDispatch(
          getPrimaryQuestionnaire(
            data?.data?.result?.isPrimary
              ? {
                  id: data?.data?.result?.id,
                  name: data?.data?.result?.questionnaireName,
                  isPrimary: data?.data?.result?.isPrimary,
                }
              : {}
          )
        );
      })
      .catch((error) => {
        loaderDispatch(setLoading(false));
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "error.somethingWentWrong")
        );
        return Promise.reject(error);
      });
  }

  static async getQuestionnaireList({
    dispatch,
    params,
    intl,
    loaderDispatch,
  }) {
    loaderDispatch(setLoading(true));
    return Http.get(PRIVATE_API_ROUTES.QUESTIONNAIRE_LISTING, params)
      .then(({ data }) => {
        loaderDispatch(setLoading(false));
        dispatch(getQuestionnaireListAction(data?.data));
      })
      .catch((err) => {
        hideLoaderShowErrorMsg({ loaderDispatch, intl, setLoading });
      });
  }

  static addEditQuestionnaire(
    dispatch,
    loaderDispatch,
    body,
    navigate,
    intl,
    editMode
  ) {
    loaderDispatch(setLoading(true));
    const request = editMode
      ? Http.put(PRIVATE_API_ROUTES.QUESTIONNAIRE, body)
      : Http.post(PRIVATE_API_ROUTES.QUESTIONNAIRE, body);
    return request
      .then((res) => {
        loaderDispatch(setLoading(false));
        showNotification(
          "success",
          "Success",
          getLocalizedMessage(
            intl,
            editMode
              ? "manageQuestionnaire.updatedSuccessfully"
              : "manageQuestionnaire.addedSuccessfully"
          )
        );
        navigate("/questionnaire");
        return Promise.resolve(res);
      })
      .catch((err) => {
        hideLoaderShowErrorMsg({ loaderDispatch, intl, setLoading });
        return Promise.reject(err);
      });
  }

  static getAllProductsCategory({ loaderDispatch, intl }) {
    loaderDispatch(setLoading(true));
    return Http.get(PRIVATE_API_ROUTES.PRODUCT_CATEGORY)
      .then((res) => {
        loaderDispatch(setLoading(false));
        return res?.data?.data;
      })
      .catch((error) => {
        hideLoaderShowErrorMsg({ loaderDispatch, intl, setLoading });
        return Promise.reject(error);
      });
  }

  static async checkSubProductCategoryAlreadyAssignedToQuestionnaire({
    loaderDispatch,
    params,
    intl,
  }) {
    loaderDispatch(setLoading(true));
    return Http.get(PRIVATE_API_ROUTES.CHECK_QUESTIONNAIRE, params)
      .then((res) => {
        loaderDispatch(setLoading(false));
        return res?.data?.data;
      })
      .catch((err) => {
        loaderDispatch(setLoading(false));
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "error.somethingWentWrong")
        );
        return Promise.reject(err);
      });
  }

  static async assignSubProductCategoryToQuestionnaire({
    loaderDispatch,
    questionnaireCode,
    body,
    intl,
  }) {
    loaderDispatch(setLoading(true));
    return Http.put(
      `${PRIVATE_API_ROUTES.ASSIGN_SUB_PRODUCT_TO_QUESTIONNAIRE}/${questionnaireCode}`,
      body
    )
      .then((res) => {
        loaderDispatch(setLoading(false));
        showNotification(
          "success",
          "Success",
          getLocalizedMessage(
            intl,
            "assigned.subProductToQuestionnaire.succesfully"
          )
        );
      })
      .catch((err) => {
        hideLoaderShowErrorMsg({ loaderDispatch, intl, setLoading });
      });
  }

  static async getQuestionnaireById({
    questionnaireDispatch,
    loaderDispatch,
    questionnaireCode,
    intl,
  }) {
    loaderDispatch(setLoading(true));
    return Http.get(PRIVATE_API_ROUTES.QUESTIONNAIRE_BY_ID(questionnaireCode))
      .then(({ data }) => {
        loaderDispatch(setLoading(false));
        questionnaireDispatch(getQuestionnaireById(data?.data));
      })
      .catch((err) => {
        hideLoaderShowErrorMsg({ loaderDispatch, intl, setLoading });
      });
  }

  static async getCountriesForQuestionnaire({
    loaderDispatch,
    productSubCategoryId,
    intl,
  }) {
    const queryString = productSubCategoryId
      ? `?productSubCategoryId=${productSubCategoryId}`
      : "";
    loaderDispatch(setLoading(true));
    return Http.get(
      `${PRIVATE_API_ROUTES.GET_COUNTRIES_FOR_QUESTIONNAIRE}${queryString}`
    )
      .then((res) => {
        loaderDispatch(setLoading(false));
        return Promise.resolve(res);
      })
      .catch((err) => {
        hideLoaderShowErrorMsg({ loaderDispatch, intl, setLoading });
      });
  }

  static async getQuestionnaireCodeForSelectedCountry({
    loaderDispatch,
    productSubCategoryId,
    countryId,
    intl,
  }) {
    const queryString = productSubCategoryId
      ? `?countryId=${countryId}&productSubCategoryId=${productSubCategoryId}`
      : `?countryId=${countryId}`;

    loaderDispatch(setLoading(true));
    return Http.get(`${PRIVATE_API_ROUTES.COUNTRY_QUESTIONNAIRE}${queryString}`)
      .then((res) => {
        loaderDispatch(setLoading(false));
        return Promise.resolve(res);
      })
      .catch((err) => {
        loaderDispatch(setLoading(false));
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "error.somethingWentWrong")
        );
      });
  }

  static async getDownloadQuestionnaireReports({
    params,
    intl,
    loaderDispatch,
  }) {
    loaderDispatch(setLoading(true));
    return Http.get(
      PRIVATE_API_ROUTES.DOWNLOAD_QUESTIONNAIRE_REPORTS,
      params,
      () => {}, //NOSONAR
      "blob"
    )
      .then((res) => {
        loaderDispatch(setLoading(false));
        downloadReports(res);
      })
      .catch(() => {
        hideLoaderShowErrorMsg({ loaderDispatch, intl, setLoading });
      });
  }
}

export default QuestionnaireService;
